import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ComboboxItem,
  Flex,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { IconDownload, IconEdit, IconLock, IconLockOpen, IconMail } from '@tabler/icons-react';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useElementSize } from '@mantine/hooks';
import { useGetPrescriptions } from '../../../global/entities/Prescription/queries/useGetPrescriptions.tsx';
import {
  TableContextProvider,
  Th,
  useTable,
} from '../../../global/hooks/useTable.tsx';
import { Prescription } from '../../../global/entities/Prescription/Prescription.ts';
import Utils from '../../../global/helpers/utils.ts';
import { useNavigate } from 'react-router-dom';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';
import { routeStore } from '../../../global/Store/Store.ts';
import { PrescriptionActivityHistory } from '../Widget/PrescriptionHistory/PrescriptionActivityHistory.tsx';
import { useValidatePrescriptions } from '../../../global/entities/Prescription/mutations/useValidatePrescriptions.tsx';
import { useDownloadPrescriptionsPdf } from '../../../global/entities/Prescription/mutations/useDownloadPrescriptionsPdf.tsx';
import { useSendPrescriptionsToAssistant } from '../../../global/entities/Prescription/mutations/useSendPrescriptionsToAssistant.tsx';
import { ValidateConfirmModal } from '../Widget/PrescriptionHistory/ValidateConfirmModal.tsx';

export type IPrescriptionHistoryProps = {
  isReponsivness?: boolean;
  patientStore?: PatientStore;
};

const PrescriptionHistory: React.FC<IPrescriptionHistoryProps> = inject(
  'patientStore',
)(
  observer(({ isReponsivness, patientStore }) => {
    const { ref, width } = useElementSize();
    const navigate = useNavigate();
    const { currentUser } = useSessionProvider();
    const { i18n } = useTranslation();

    const patientId = patientStore?.patientId || currentUser?.patientID;
    const isHCP = !!currentUser?.healthcareProfessionalID;
    const { data: prescriptionData, refetch: refetchPrescriptions } =
      useGetPrescriptions(patientId);
    const validateMutation = useValidatePrescriptions(patientId);
    const downloadPdfMutation = useDownloadPrescriptionsPdf();
    const sendToAssistantMutation = useSendPrescriptionsToAssistant();
    const [validateAllOpen, setValidateAllOpen] = useState(false);
    const {
      sortedData,
      setSortedData,
      sortBy,
      reverseSortDirection,
      setSorting,
    } = useTable();

    const draftPrescriptions = useMemo(
      () => (prescriptionData?.prescriptions ?? []).filter((p) => !p.isLocked),
      [prescriptionData?.prescriptions],
    );

    const unitsLabel = (unitID: number): string | undefined =>
      prescriptionData?.unitsCombobox.find((u) => Number(u.value) === unitID)
        ?.label;

    const handleDownloadPdf = () => {
      if (!patientId) return;
      downloadPdfMutation.mutate({
        patientId: String(patientId),
        language: i18n.language,
        patientName: patientStore?.name,
        patientDateOfBirth: patientStore?.birthDate,
      });
    };

    const handleSendToAssistant = () => {
      if (!patientId) return;
      sendToAssistantMutation.mutate({
        patientId: String(patientId),
        language: i18n.language,
      });
    };

    const confirmValidateAll = async () => {
      if (!patientId) return;
      try {
        await validateMutation.mutateAsync({
          patientID: Number(patientId),
          all: true,
        });
        setValidateAllOpen(false);
        await refetchPrescriptions();
      } catch (e) {
        setValidateAllOpen(false);
        console.error(e);
      }
    };

    useEffect(() => {
      setSortedData([]);
      if (prescriptionData?.prescriptions?.length)
        setSortedData(prescriptionData?.prescriptions);
    }, [prescriptionData?.prescriptions]);

    const onClickEditPrescription = () => {
      navigate(
        `editPrescription/${patientStore?.patientId || currentUser?.patientID}`,
      );
    };

    useEffect(() => {
      routeStore?.setFromLocation();
    }, []);

    const truncate = {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };

    const total = prescriptionData?.totalCount ?? 0;
    const locked = prescriptionData?.lockedCount ?? 0;
    const mostRecent = prescriptionData?.mostRecentValidatedAt;
    const formattedMostRecent = mostRecent
      ? mostRecent.toLocaleString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
          dateStyle: 'medium',
          timeStyle: 'short',
        })
      : '';

    const renderSectionStatus = () => {
      if (total === 0) return null;
      if (locked === 0) {
        return (
          <Badge
            variant="light"
            color="gray"
            leftSection={<IconLockOpen size={12} />}
          >
            {t('prescriptionHistory.status.draft')}
          </Badge>
        );
      }
      if (locked < total) {
        return (
          <Badge
            variant="light"
            color="blue"
            leftSection={<IconLock size={12} />}
          >
            {t('prescriptionHistory.status.partial', { locked, total })}
          </Badge>
        );
      }
      return (
        <Tooltip
          label={
            mostRecent
              ? t('prescriptionHistory.status.lastValidated', {
                  timestamp: formattedMostRecent,
                })
              : ''
          }
          disabled={!mostRecent}
          withArrow
        >
          <Badge
            variant="filled"
            color="green"
            leftSection={<IconLock size={12} />}
          >
            {t('prescriptionHistory.status.allLocked')}
          </Badge>
        </Tooltip>
      );
    };

    const formatValidatorTooltip = (row: Prescription) => {
      if (!row.isLocked) return '';
      const ts = row.validatedAtUtc
        ? row.validatedAtUtc.toLocaleString(
            i18n.language === 'fr' ? 'fr-CA' : 'en-CA',
            {
              dateStyle: 'medium',
              timeStyle: 'short',
            },
          )
        : '';
      return t('prescriptionHistory.badge.validatedBy', {
        name: row.validatedByName ?? '—',
        timestamp: ts,
      });
    };

    return (
      <Paper h={'100%'} w={'100%'} ref={ref}>
        <Stack>
          <Flex justify={'space-between'} align="center" wrap="wrap" gap="sm">
            <Group gap="sm">
              <Title order={4}>
                {t('table.prescriptionHistory.prescriptionHistory')}
              </Title>
              {renderSectionStatus()}
            </Group>
            <Group gap="sm">
              {isHCP && draftPrescriptions.length > 0 && (
                <Button
                  hidden={width <= 500}
                  onClick={() => setValidateAllOpen(true)}
                  variant="filled"
                  color="green"
                  leftSection={<IconLock size={14} />}
                >
                  {t('prescriptionHistory.actions.validateAll')}
                </Button>
              )}
              <Button
                hidden={width <= 500}
                onClick={onClickEditPrescription}
                variant="filled"
                leftSection={<IconEdit size={14} />}
              >
                {t('table.prescriptionHistory.addNewPrescription')}
              </Button>
            </Group>
          </Flex>
          <ScrollArea w="100%">
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Th
                    sorted={sortBy === 'drugName'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('drugName', sortedData)}
                  >
                    {t('table.prescriptionHistory.medication')}
                  </Th>
                  <Th
                    sorted={sortBy === 'dose'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('dose', sortedData)}
                  >
                    {t('table.prescriptionHistory.dose')}
                  </Th>
                  <Th
                    sorted={sortBy === 'unitID'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('unitID', sortedData)}
                  >
                    {t('table.prescriptionHistory.unit')}
                  </Th>
                  <Th
                    sorted={sortBy === 'doseFrequencyID'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('doseFrequencyID', sortedData)}
                  >
                    {t('table.prescriptionHistory.frequency')}
                  </Th>
                  <Th
                    sorted={sortBy === 'startDate'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('startDate', sortedData)}
                  >
                    <Stack gap={'xs'} ta={'start'}>
                      {t('table.prescriptionHistory.startDate')}
                      <Text size={'xs'} c={'dimmed'}>
                        {t('placeHolder.dateFormat')}
                      </Text>
                    </Stack>
                  </Th>
                  <Th
                    sorted={sortBy === 'endDate'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('endDate', sortedData)}
                  >
                    <Stack ta={'start'} gap={'xs'}>
                      {t('table.prescriptionHistory.endDate')}
                      <Text size={'xs'} c={'dimmed'}>
                        {t('placeHolder.dateFormat')}
                      </Text>
                    </Stack>
                  </Th>
                  <Table.Th>
                    {t('prescriptionHistory.badge.columnHeader')}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sortedData?.map((row: Prescription, index) => (
                  <Table.Tr
                    key={row.id}
                    style={{
                      background:
                        index % 2 === 1
                          ? 'var(--Background-Gray-light, #E9ECEF)'
                          : undefined,
                    }}
                  >
                    <Table.Td maw={200} style={truncate}>
                      {row.drugName}
                    </Table.Td>
                    <Table.Td>{row.dose}</Table.Td>
                    <Table.Td>
                      {
                        prescriptionData?.unitsCombobox.find(
                          (el) => Number(el.value) === row.unitID,
                        )?.label
                      }
                    </Table.Td>
                    <Table.Td hidden={width < 390 && isReponsivness}>
                      {
                        prescriptionData?.doseFrequenciesCombobox.find(
                          (el: ComboboxItem) =>
                            Number(el.value) === row.doseFrequencyID,
                        )?.label
                      }
                    </Table.Td>
                    <Table.Td hidden={width < 585 && isReponsivness}>
                      {Utils.formatDateToYYYYMMDD(row.startDate)}
                    </Table.Td>
                    <Table.Td hidden={width < 670 && isReponsivness}>
                      {row.endDate
                        ? Utils.formatDateToYYYYMMDD(row.endDate)
                        : t('prescriptionHistory.ongoing')}
                    </Table.Td>
                    <Table.Td>
                      {row.isLocked ? (
                        <Tooltip
                          label={formatValidatorTooltip(row)}
                          withArrow
                          multiline
                          w={220}
                        >
                          <Badge
                            color="green"
                            variant="light"
                            leftSection={<IconLock size={12} />}
                          >
                            {t('prescriptionHistory.badge.locked')}
                          </Badge>
                        </Tooltip>
                      ) : (
                        <Badge
                          color="gray"
                          variant="light"
                          leftSection={<IconLockOpen size={12} />}
                        >
                          {t('prescriptionHistory.badge.draft')}
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          {isHCP && draftPrescriptions.length > 0 && (
            <Button
              hidden={width > 500}
              onClick={() => setValidateAllOpen(true)}
              variant="filled"
              color="green"
              leftSection={<IconLock size={14} />}
            >
              {t('prescriptionHistory.actions.validateAll')}
            </Button>
          )}
          <Button
            hidden={width > 500}
            onClick={onClickEditPrescription}
            variant="filled"
            leftSection={<IconEdit size={14} />}
          >
            {t('table.prescriptionHistory.addNewPrescription')}
          </Button>
          {isHCP && (prescriptionData?.prescriptions?.length ?? 0) > 0 && (
            <>
              <Flex justify="flex-end" gap="sm" wrap="wrap" mt="md">
                <Button
                  variant="light"
                  leftSection={<IconDownload size={16} />}
                  onClick={handleDownloadPdf}
                  loading={downloadPdfMutation.isPending}
                >
                  {t('prescriptionHistory.actions.downloadPdf')}
                </Button>
                <Button
                  variant="light"
                  color="grape"
                  leftSection={<IconMail size={16} />}
                  onClick={handleSendToAssistant}
                  loading={sendToAssistantMutation.isPending}
                >
                  {t('prescriptionHistory.actions.sendToAssistant')}
                </Button>
              </Flex>
            </>
          )}
          {patientId && isHCP && <PrescriptionActivityHistory patientId={patientId} />}
        </Stack>
        <ValidateConfirmModal
          opened={validateAllOpen}
          onClose={() => setValidateAllOpen(false)}
          onConfirm={confirmValidateAll}
          prescriptions={draftPrescriptions}
          validatingHcpName={currentUser?.displayName ?? ''}
          isLoading={validateMutation.isPending}
          unitsLabel={unitsLabel}
        />
      </Paper>
    );
  }),
);

const WrappedPrescriptionHistoryTable: React.FC = () => {
  return (
    <TableContextProvider>
      <PrescriptionHistory />
    </TableContextProvider>
  );
};

export default WrappedPrescriptionHistoryTable;
